import type { DaySchedule, ActivityEntry } from '@crm-dance/public-api';

/**
 * Pure date / time helpers for the embed.
 *
 * <p>Functions in this file are <strong>copied verbatim</strong> from
 * {@code websites/packages/schedule-widget/src/utils.ts} with identical
 * signatures and identical fixture output. The duplication is deliberate —
 * the two packages run on different runtimes (Preact + Shadow DOM here vs.
 * React Server Component + Tailwind there) and a shared dependency would
 * force one to compromise.
 *
 * <p>Drift detection: a snapshot test in <em>both</em> packages feeds the
 * same fake {@link PublicScheduleResponse} and asserts identical day-key
 * + time-string output. If a downstream change makes one package's output
 * drift from the other, the test fails on the unchanged package too.
 *
 * @see docs/specs/schedule-widget-embed/CLAUDE.md § Shared invariants
 */

/** Convert 24h time "HH:mm" to 12h format "7:00 PM". */
export function formatTime12(time: string): string {
	if (!time || !time.includes(':')) return '';
	const [h, m] = time.split(':').map(Number);
	const suffix = h !== undefined && h >= 12 ? 'PM' : 'AM';
	const hour12 = h !== undefined ? h % 12 || 12 : 0;
	return m === 0 ? `${hour12} ${suffix}` : `${hour12}:${String(m).padStart(2, '0')} ${suffix}`;
}

/**
 * Duration in minutes between two "HH:mm" times. Returns 0 for malformed
 * input rather than NaN — the row hides the duration when 0.
 */
export function durationMinutes(start: string, end: string): number {
	const parse = (t: string): number | null => {
		if (!t || !t.includes(':')) return null;
		const [h, m] = t.split(':').map(Number);
		if (h === undefined || m === undefined || Number.isNaN(h) || Number.isNaN(m)) return null;
		return h * 60 + m;
	};
	const s = parse(start);
	const e = parse(end);
	if (s === null || e === null) return 0;
	return Math.max(0, e - s);
}

/**
 * Format an ISO date "YYYY-MM-DD" as a human day heading: "Friday, May 9".
 *
 * <p>Uses {@code Intl.DateTimeFormat} so we don't ship date-fns; the locale
 * is pinned to en-US for v1 (see scope cuts in CLAUDE.md).
 */
export function formatDayHeading(isoDate: string): string {
	if (!isoDate) return '';
	// Append T00:00:00 so the date is parsed at local midnight, not UTC midnight
	// (which can flip to the previous day in negative-offset timezones).
	const d = new Date(`${isoDate}T00:00:00`);
	if (Number.isNaN(d.getTime())) return isoDate;
	return d.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' });
}

/**
 * Filter a schedule response to the days that actually have entries, in
 * chronological order. The v1 list leaves out classes another studio runs
 * (an independent teacher's, `hostedBy` set): its Book link knows one slug. Days with empty / missing entries are dropped from
 * the render, but the underlying response shape is preserved (we don't
 * mutate the input).
 */
export function visibleDays(days: DaySchedule[] | undefined | null): Array<DaySchedule & { entries: ActivityEntry[] }> {
	if (!days || days.length === 0) return [];
	return days
		.map((d) => ({ ...d, entries: (d.entries ?? []).filter((entry) => entry.hostedBy == null) }))
		.filter((d) => d.entries.length > 0)
		.sort((a, b) => (a.date ?? '').localeCompare(b.date ?? ''));
}

/**
 * Build the booking URL for an activity. Hardcoded to {@code app.crm.dance}
 * — see technical.md § Booking URL for why this lives in the bundle, not
 * the customer's snippet.
 */
export function buildBookingUrl(slug: string, activityId: string): string {
	return `https://app.crm.dance/p/${encodeURIComponent(slug)}/activity/${encodeURIComponent(activityId)}`;
}

const MONTH_SHORT_LABELS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const DATE_KEY_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/;

function parseDateKey(dateKey: string): { year: number; month: number; day: number } | null {
	const parts = DATE_KEY_PATTERN.exec(dateKey);
	if (!parts) return null;
	const year = Number(parts[1]);
	const month = Number(parts[2]);
	const day = Number(parts[3]);
	if (month < 1 || month > 12 || day < 1 || day > 31) return null;
	return { year, month, day };
}

/**
 * Format a multi-day event's inclusive date span from two "YYYY-MM-DD" keys:
 * "Jul 24 – 27" (same month), "Jul 24 – Aug 2" (cross-month), "Jul 24" (single
 * day or one side missing/invalid), '' when neither side parses.
 *
 * <p>Copied verbatim from {@code schedule-widget/src/utils.ts} — see the file
 * header for the deliberate-duplication + drift-test contract.
 */
export function formatDateSpan(startDate?: string | null, endDate?: string | null): string {
	const start = startDate ? parseDateKey(startDate) : null;
	const end = endDate ? parseDateKey(endDate) : null;

	const shortLabel = (p: { month: number; day: number }): string => `${MONTH_SHORT_LABELS[p.month - 1]} ${p.day}`;

	if (start && end) {
		if (start.year === end.year && start.month === end.month && start.day === end.day) return shortLabel(start);
		if (start.year === end.year && start.month === end.month) return `${shortLabel(start)} – ${end.day}`;
		return `${shortLabel(start)} – ${shortLabel(end)}`;
	}
	if (start) return shortLabel(start);
	if (end) return shortLabel(end);
	return '';
}
